<template>
  <div class="pa-4 pa-md-6">
    <div class="grey--text" style="font-size: 13px">Otso Panalo Game</div>
    <h2 class="font-weight-bold mb-3 mb-md-5">Create Game</h2>

    <v-card class="shadow">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6">Game Setup</span>
        <v-btn text color="secondary" @click="goBack">
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="py-4">
        <v-alert v-if="errorMessage" type="error" dense outlined class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <v-alert type="info" dense outlined class="mb-4">
          New Otso Panalo games start with the status Not yet started and
          betting disabled.
        </v-alert>

        <v-alert
          v-if="showProgressiveJackpotAlert"
          type="info"
          dense
          outlined
          class="mb-4"
        >
          {{ jackpotAlertMessage }}
        </v-alert>

        <v-alert
          v-if="!loadingGameConfig && !gameConfig"
          type="warning"
          dense
          outlined
          class="mb-4"
        >
          No active Otso Panalo game config is available. Activate a game config
          first before creating a game.
        </v-alert>

        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          @submit.prevent="handleSubmit"
        >
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                ref="endingGame"
                v-model="form.ending_game_id"
                :items="availableGames"
                :rules="[rules.required]"
                item-text="display_name"
                item-value="ending_game_id"
                label="Available Ending Game"
                placeholder="Select an ending game"
                :loading="loadingAvailableGames"
                :disabled="loadingAvailableGames || submitLoading"
                no-data-text="No available ending game found"
                outlined
                dense
                hide-details="auto"
              ></v-autocomplete>
            </v-col>
          </v-row>

          <v-row v-if="selectedGame" class="mt-1">
            <v-col cols="12">
              <v-sheet class="pa-3 grey lighten-5 rounded">
                <div class="text-caption grey--text text--darken-1 mb-1">
                  Selected Ending Game
                </div>
                <div class="font-weight-medium">{{ selectedGame.league }}</div>
                <div>
                  {{ selectedGame.team_a_name }}
                  {{ selectedGame.team_a_alias }} vs
                  {{ selectedGame.team_b_name }} {{ selectedGame.team_b_alias }}
                </div>
                <div class="text-caption mt-1">
                  Schedule: {{ formatDate(selectedGame.schedule, true) }}
                </div>
                <div class="text-caption mt-1">
                  Betting Deadline:
                  {{ selectedGameBettingDeadlineText || "-" }}
                </div>
              </v-sheet>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col cols="12">
              <v-text-field
                :value="selectedGameBettingDeadlineText"
                label="Betting Deadline"
                placeholder="Select an ending game first"
                readonly
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-text-field
                value="Disabled"
                label="Betting"
                readonly
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <div class="text-overline mb-2">Active Game Config</div>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :value="displayBetAmount"
                label="Bet Amount"
                prefix="PHP"
                readonly
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :value="displayPoolPercentage"
                label="Pool Percentage"
                suffix="%"
                readonly
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                :value="displayJackpotPrize"
                label="Jackpot Prize"
                prefix="PHP"
                readonly
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :value="displaySecondPrize"
                label="Second Prize"
                prefix="PHP"
                readonly
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                :value="displayThirdPrize"
                label="Third Prize"
                prefix="PHP"
                readonly
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :value="displayFourthPrize"
                label="Fourth Prize"
                prefix="PHP"
                readonly
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row dense class="mt-2">
            <v-col cols="12" md="6">
              <v-btn
                block
                color="primary"
                type="submit"
                :disabled="!valid || !availableGames.length || !gameConfig"
                :loading="submitLoading"
              >
                Save Game
              </v-btn>
            </v-col>
            <v-col cols="12" md="6">
              <v-btn
                block
                outlined
                color="black"
                :disabled="submitLoading"
                @click="goBack"
              >
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import formRules from "@/helpers/formRules";
import formatDate from "@/helpers/formatDate";
import responseGet from "@/helpers/api_request_get";
import responsePost from "@/helpers/api_request_post";

export default {
  data: () => ({
    valid: true,
    rules: formRules,
    loadingAvailableGames: false,
    loadingGameConfig: false,
    submitLoading: false,
    errorMessage: "",
    availableGames: [],
    gameConfig: null,
    form: {
      ending_game_id: "",
      bet_amount: "0.00",
      jackpot_prize: "0.00",
      second_prize: "0.00",
      third_prize: "0.00",
      fourth_prize: "0.00",
      pool_percentage: "0.00",
    },
  }),

  computed: {
    selectedGame() {
      return (
        this.availableGames.find(
          (game) => game.ending_game_id === this.form.ending_game_id,
        ) || null
      );
    },

    selectedGameBettingDeadlineText() {
      return this.selectedGame && this.selectedGame.ending_betting_deadline
        ? formatDate(this.selectedGame.ending_betting_deadline, true)
        : "";
    },

    displayJackpotPrize() {
      if (!this.gameConfig) {
        return "";
      }

      return this.formatMoney(
        this.gameConfig.effective_jackpot_prize ||
          this.gameConfig.jackpot_prize ||
          "",
      );
    },

    displayBetAmount() {
      return this.gameConfig
        ? this.formatMoney(this.gameConfig.bet_amount)
        : "";
    },

    displayPoolPercentage() {
      return this.gameConfig
        ? this.formatPercent(this.gameConfig.pool_percentage)
        : "";
    },

    displaySecondPrize() {
      return this.gameConfig
        ? this.formatMoney(this.gameConfig.second_prize)
        : "";
    },

    displayThirdPrize() {
      return this.gameConfig
        ? this.formatMoney(this.gameConfig.third_prize)
        : "";
    },

    displayFourthPrize() {
      return this.gameConfig
        ? this.formatMoney(this.gameConfig.fourth_prize)
        : "";
    },

    showProgressiveJackpotAlert() {
      return Boolean(
        this.gameConfig &&
          this.gameConfig.progressive_reason &&
          this.gameConfig.progressive_reason !== "default_config",
      );
    },

    jackpotAlertMessage() {
      if (!this.gameConfig) {
        return "";
      }

      const jackpotPrize = this.displayJackpotPrize;
      const previousGameId = this.gameConfig.previous_game_id;

      switch (this.gameConfig.progressive_reason) {
        case "pending_previous_game":
          return previousGameId
            ? `Previous game #${previousGameId} is not yet approved, so this game keeps the current jackpot of PHP ${jackpotPrize}.`
            : `This game keeps the current jackpot of PHP ${jackpotPrize}.`;

        case "rollover_added":
          return previousGameId
            ? `This game will use the Progressive Jackpot of PHP ${jackpotPrize}, including PHP ${this.formatMoney(
                this.gameConfig.rollover_amount,
              )} carried over from game #${previousGameId}.`
            : `This game will use the Progressive Jackpot of PHP ${jackpotPrize}.`;

        case "no_remaining_pool":
          return previousGameId
            ? `Game #${previousGameId} had no jackpot winner, but there was no remaining pool to add. This game keeps the jackpot at PHP ${jackpotPrize}.`
            : `There was no remaining pool to add, so this game keeps the jackpot at PHP ${jackpotPrize}.`;

        case "jackpot_won_reset":
          return `The previous jackpot was won, so this game resets to the default jackpot of PHP ${jackpotPrize}.`;

        default:
          return `This game will use the jackpot of PHP ${jackpotPrize}.`;
      }
    },
  },

  created() {
    this.getAvailableGames();
    this.getActiveGameConfig();
  },

  methods: {
    formatDate,

    formatMoney(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      const amount = Number(value);

      if (Number.isNaN(amount)) {
        return value;
      }

      return amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatPercent(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      const amount = Number(value);

      if (Number.isNaN(amount)) {
        return value;
      }

      return amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    async getAvailableGames() {
      try {
        this.loadingAvailableGames = true;
        this.errorMessage = "";

        const response = await responseGet(
          "",
          "/otso_panalo/games/available_ending_games",
        );

        if (response.status === 200) {
          this.availableGames = (response.data.records || []).map((game) => ({
            ...game,
            display_name: `${game.league} | ${game.team_a_name} ${
              game.team_a_alias
            } vs ${game.team_b_name} ${game.team_b_alias} | ${formatDate(
              game.schedule,
              true,
            )}`,
          }));

          this.$nextTick(() => {
            if (this.$refs.endingGame) {
              this.$refs.endingGame.focus();
            }
          });
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.availableGames = [];
          this.errorMessage = "Failed to load available ending games.";
        }
      } catch (error) {
        this.availableGames = [];
        this.errorMessage = "Failed to load available ending games.";
      } finally {
        this.loadingAvailableGames = false;
      }
    },

    async getActiveGameConfig() {
      try {
        this.loadingGameConfig = true;

        const response = await responseGet(
          "",
          "/otso_panalo/game_configs/active",
        );

        if (response.status === 200) {
          this.gameConfig = response.data.details || null;
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.gameConfig = null;
        }
      } catch (error) {
        this.gameConfig = null;
      } finally {
        this.loadingGameConfig = false;
      }
    },

    getMessage(payload, fallback) {
      if (!payload) {
        return fallback;
      }

      if (payload.messages) {
        const firstKey = Object.keys(payload.messages)[0];
        if (firstKey && typeof payload.messages[firstKey] === "string") {
          return payload.messages[firstKey];
        }
      }

      if (typeof payload.message === "string") {
        return payload.message;
      }

      return fallback;
    },

    async handleSubmit() {
      if (!this.$refs.form.validate() || this.submitLoading) {
        return;
      }

      if (!this.gameConfig) {
        this.errorMessage =
          "No active Otso Panalo game config is available for this game.";
        return;
      }

      try {
        this.submitLoading = true;
        this.errorMessage = "";

        const formData = new FormData();
        formData.append("ending_game_id", this.form.ending_game_id);
        formData.append("betting", "Disabled");

        const response = await responsePost(formData, "otso_panalo/games");

        if (response.status === 200) {
          const gameId = response.data && response.data.id;

          if (gameId) {
            this.$router.push({
              name: "OtsoPanaloGameDetails",
              params: { id: gameId },
            });
          } else {
            this.$router.push({
              name: "OtsoPanaloGames",
              query: { created: "1" },
            });
          }
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.errorMessage = this.getMessage(
            response.data,
            "Failed to create Otso Panalo game.",
          );
        }
      } catch (error) {
        this.errorMessage = "Failed to create Otso Panalo game.";
      } finally {
        this.submitLoading = false;
      }
    },

    goBack() {
      this.$router.push({ name: "OtsoPanaloGames" });
    },
  },
};
</script>
